import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useUserRole } from '../hooks/useUserRole';
import { toastSuccess, toastError } from '../ui/toast';
import CipherHeader from './ui/CipherHeader';
import { colors as theme } from '../theme';

const MyCoaches = ({ onBack }) => {
  const { role, user } = useUserRole();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState(null); // drill-in view
  const [coachClients, setCoachClients] = useState([]);
  const [coachPrograms, setCoachPrograms] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null); // client being reassigned
  const [reassignCoachId, setReassignCoachId] = useState('');
  const [savingReassign, setSavingReassign] = useState(false);

  useEffect(() => {
    if (role && user) loadCoaches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user]);

  const loadCoaches = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('users')
        .select('id, auth_user_id, name, email, role, status, coach_code, created_at, admin_id')
        .eq('role', 'coach')
        .order('name');

      // Admins only see their own coaches; super admin sees all
      if (role === 'admin') {
        query = query.eq('admin_id', user.auth_user_id);
      }

      const { data: coachList, error } = await query;
      if (error) throw error;

      // Batched client/program counts for all coaches in 2 queries total, instead of 2 per
      // coach (same anti-pattern already fixed elsewhere — see AdminProgramViewer.jsx).
      const coachIds = (coachList || []).map(c => c.auth_user_id);
      let clientCountMap = {};
      let programCountMap = {};

      if (coachIds.length > 0) {
        const [{ data: allClients }, { data: allPrograms }] = await Promise.all([
          supabase.from('users').select('coach_id').in('coach_id', coachIds).eq('role', 'client'),
          supabase.from('programs').select('user_id').in('user_id', coachIds).eq('is_client_copy', false),
        ]);
        clientCountMap = (allClients || []).reduce((map, c) => {
          map[c.coach_id] = (map[c.coach_id] || 0) + 1;
          return map;
        }, {});
        programCountMap = (allPrograms || []).reduce((map, p) => {
          map[p.user_id] = (map[p.user_id] || 0) + 1;
          return map;
        }, {});
      }

      const enriched = (coachList || []).map((coach) => ({
        ...coach,
        clientCount: clientCountMap[coach.auth_user_id] || 0,
        programCount: programCountMap[coach.auth_user_id] || 0,
      }));

      setCoaches(enriched);
    } catch (err) {
      console.error('Error loading coaches:', err);
      toastError('Could not load coaches');
    } finally {
      setLoading(false);
    }
  };

  const openCoachDetail = async (coach) => {
    setSelectedCoach(coach);
    setLoadingDetail(true);
    try {
      const [{ data: clients, error: clientsErr }, { data: programs, error: programsErr }] = await Promise.all([
        supabase
          .from('users')
          .select('id, name, email, status, created_at')
          .eq('coach_id', coach.auth_user_id)
          .eq('role', 'client')
          .order('name'),
        supabase
          .from('programs')
          .select('id, name, created_at, archived_at')
          .eq('user_id', coach.auth_user_id)
          .eq('is_client_copy', false)
          .is('archived_at', null)
          .order('created_at', { ascending: false })
      ]);
      if (clientsErr) throw clientsErr;
      if (programsErr) throw programsErr;
      setCoachClients(clients || []);
      setCoachPrograms(programs || []);
    } catch (err) {
      console.error('Error loading coach detail:', err);
      toastError('Could not load coach details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignTarget || !reassignCoachId) return;
    setSavingReassign(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ coach_id: reassignCoachId })
        .eq('id', reassignTarget.id);
      if (error) throw error;
      toastSuccess(`${reassignTarget.name} reassigned successfully`);
      setReassignTarget(null);
      setReassignCoachId('');
      // Refresh detail view
      await openCoachDetail(selectedCoach);
    } catch (err) {
      console.error('Reassign error:', err);
      toastError('Could not reassign client');
    } finally {
      setSavingReassign(false);
    }
  };

  const statusBadge = (status) => {
    const colors = {
      active: { bg: '#1a3a1a', color: theme.success, text: 'Active' },
      pending: { bg: '#3a2a00', color: theme.warning, text: 'Pending' },
      suspended: { bg: '#3a0000', color: theme.danger, text: 'Suspended' },
      rejected: { bg: '#2a1a1a', color: theme.danger, text: 'Rejected' },
    };
    const s = colors[status] || { bg: theme.surface2, color: theme.textMuted, text: status };
    return (
      <span style={{
        backgroundColor: s.bg,
        color: s.color,
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 700
      }}>{s.text}</span>
    );
  };

  // ── Detail view ──────────────────────────────────────────────────────────────
  if (selectedCoach) {
    // Coaches available for reassignment (all coaches in scope except the current one)
    const otherCoaches = coaches.filter(c => c.id !== selectedCoach.id);

    return (
      <div style={{ padding: '30px', backgroundColor: theme.bg, minHeight: '100vh' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '30px' }}>
          <button
            onClick={() => { setSelectedCoach(null); setReassignTarget(null); }}
            style={{
              background: 'transparent', border: `1px solid ${theme.surface3}`,
              color: theme.gold, padding: '8px 16px', borderRadius: '8px',
              cursor: 'pointer', fontSize: '14px'
            }}
          >
            Back
          </button>
          <div>
            <h2 style={{ color: theme.gold, margin: 0, fontSize: '24px' }}>{selectedCoach.name}</h2>
            <p style={{ color: theme.textMuted, margin: '4px 0 0', fontSize: '14px' }}>
              {selectedCoach.email} · {selectedCoach.coach_code || '—'}
            </p>
          </div>
          {statusBadge(selectedCoach.status)}
        </div>

        {loadingDetail ? (
          <p style={{ color: theme.textMuted }}>Loading...</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', maxWidth: '900px' }}>

            {/* Clients */}
            <div style={{ backgroundColor: theme.surface1, border: `1px solid ${theme.surface3}`, borderRadius: '14px', padding: '20px' }}>
              <h3 style={{ color: theme.gold, marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>
                Clients ({coachClients.length})
              </h3>
              {coachClients.length === 0 ? (
                <p style={{ color: theme.textMuted, fontSize: '14px' }}>No clients assigned</p>
              ) : coachClients.map(client => (
                <div key={client.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: `1px solid ${theme.surface3}`
                }}>
                  <div>
                    <div style={{ color: theme.text, fontSize: '14px', fontWeight: 700 }}>{client.name}</div>
                    <div style={{ color: theme.textMuted, fontSize: '12px' }}>{client.email}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {statusBadge(client.status)}
                    {otherCoaches.length > 0 && (
                      <button
                        onClick={() => { setReassignTarget(client); setReassignCoachId(''); }}
                        style={{
                          background: 'transparent', border: '1px solid #555',
                          color: theme.textMuted, padding: '4px 8px', borderRadius: '8px',
                          cursor: 'pointer', fontSize: '11px'
                        }}
                      >
                        Reassign
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Programs */}
            <div style={{ backgroundColor: theme.surface1, border: `1px solid ${theme.surface3}`, borderRadius: '14px', padding: '20px' }}>
              <h3 style={{ color: theme.gold, marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>
                Programs ({coachPrograms.length})
              </h3>
              {coachPrograms.length === 0 ? (
                <p style={{ color: theme.textMuted, fontSize: '14px' }}>No programs created</p>
              ) : coachPrograms.map(program => (
                <div key={program.id} style={{
                  padding: '10px 0', borderBottom: `1px solid ${theme.surface3}`
                }}>
                  <div style={{ color: theme.text, fontSize: '14px', fontWeight: 700 }}>{program.name}</div>
                  <div style={{ color: theme.textMuted, fontSize: '12px' }}>
                    Created {new Date(program.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reassign modal */}
        {reassignTarget && (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              backgroundColor: theme.surface1, borderRadius: '14px',
              padding: '30px', width: '380px', border: `1px solid ${theme.surface3}`
            }}>
              <h3 style={{ color: theme.gold, marginTop: 0 }}>Reassign Client</h3>
              <p style={{ color: theme.text, marginBottom: '8px' }}>
                Move <strong>{reassignTarget.name}</strong> to:
              </p>
              <select
                value={reassignCoachId}
                onChange={e => setReassignCoachId(e.target.value)}
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  backgroundColor: theme.surface1, border: `1px solid ${theme.surface3}`,
                  color: theme.text, fontSize: '14px', marginBottom: '20px'
                }}
              >
                <option value="">— Select a coach —</option>
                {otherCoaches.map(c => (
                  <option key={c.id} value={c.auth_user_id}>{c.name} ({c.clientCount} clients)</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleReassign}
                  disabled={!reassignCoachId || savingReassign}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                    backgroundColor: reassignCoachId ? theme.gold : '#555',
                    color: theme.surface1, fontWeight: 700, cursor: reassignCoachId ? 'pointer' : 'not-allowed'
                  }}
                >
                  {savingReassign ? 'Saving...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setReassignTarget(null)}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '8px',
                    border: `1px solid ${theme.surface3}`, backgroundColor: 'transparent',
                    color: theme.textMuted, cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Coach list view ──────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '30px', backgroundColor: theme.bg, minHeight: '100vh' }}>
      <CipherHeader title="My Coaches" subtitle="View coaches & clients" onBack={onBack} />

      {loading ? (
        <p style={{ color: theme.textMuted }}>Loading coaches...</p>
      ) : coaches.length === 0 ? (
        <div style={{
          backgroundColor: theme.surface1, border: `1px solid ${theme.surface3}`, borderRadius: '14px', padding: '40px',
          textAlign: 'center', maxWidth: '500px'
        }}>
          <p style={{ color: theme.textMuted, fontSize: '16px', margin: 0 }}>
            No coaches yet. Create coach accounts via People &rarr; Create Coach.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', maxWidth: '960px' }}>
          {coaches.map(coach => (
            <div
              key={coach.id}
              onClick={() => openCoachDetail(coach)}
              style={{
                backgroundColor: theme.surface1, borderRadius: '14px', padding: '20px',
                border: `1px solid ${theme.surface3}`, cursor: 'pointer', transition: 'border-color 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = theme.gold}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.surface3}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ color: theme.text, fontSize: '16px', fontWeight: 700 }}>{coach.name}</div>
                {statusBadge(coach.status)}
              </div>
              <div style={{ color: theme.textMuted, fontSize: '13px', marginBottom: '4px' }}>{coach.email}</div>
              {coach.coach_code && (
                <div style={{
                  display: 'inline-block', backgroundColor: theme.surface1, color: theme.gold,
                  fontFamily: 'monospace', fontSize: '12px', padding: '2px 8px',
                  borderRadius: '4px', marginBottom: '12px'
                }}>
                  {coach.coach_code}
                </div>
              )}
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${theme.surface3}` }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: theme.gold, fontSize: '20px', fontWeight: 700 }}>{coach.clientCount}</div>
                  <div style={{ color: theme.textMuted, fontSize: '11px' }}>Clients</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: theme.gold, fontSize: '20px', fontWeight: 700 }}>{coach.programCount}</div>
                  <div style={{ color: theme.textMuted, fontSize: '11px' }}>Programs</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCoaches;
